import type { ImportantDateType, ImportantDateWithOccurrence, Person, TargetType } from '../api/types';

const koreanDateFormatter = new Intl.DateTimeFormat('ko-KR', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  weekday: 'short',
});

const parseDateStringAsLocal = (date: string) => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  return new Date(date);
};

export const formatOccurrenceDate = (date: string) =>
  koreanDateFormatter
    .format(parseDateStringAsLocal(date))
    .replace(/\. /g, '.')
    .replace(/\.$/, '')
    .replace(' ', '');

const formatTargetTypeLabel = (targetType: TargetType) => (targetType === 'pet' ? '반려동물' : '사람');

export const formatPersonMeta = (person: Pick<Person, 'targetType' | 'relation' | 'category'>) =>
  [person.relation, person.category, formatTargetTypeLabel(person.targetType)].filter(Boolean).join(' · ');

export const formatImportantDateTypeLabel = (type: ImportantDateType) => {
  const labels: Record<ImportantDateType, string> = {
    birthday: '생일',
    anniversary: '기념일',
    memorial: '추모일',
    petBirthday: '반려동물 생일',
    adoption: '입양일',
    vaccination: '예방접종',
    appointment: '진료 예약',
    dday: 'D-day',
    custom: '직접 입력',
  };
  return labels[type];
};

export const formatOriginalDateLabel = (
  entry: Pick<ImportantDateWithOccurrence, 'originalYear' | 'originalMonth' | 'originalDay' | 'calendarType' | 'isLeapMonth'>
) => {
  const calendar = entry.calendarType === 'lunar' ? `음력${entry.isLeapMonth ? ' 윤달' : ''}` : '양력';
  const year = entry.originalYear ? `${entry.originalYear}/` : '';
  return `${calendar} ${year}${entry.originalMonth}/${entry.originalDay}`;
};

export const formatDaysLeft = (daysLeft: number, fallbackLabel?: string) => {
  if (fallbackLabel) return fallbackLabel;
  if (daysLeft === 0) return '오늘';
  if (daysLeft === 1) return '내일';
  if (daysLeft < 0) return `D+${Math.abs(daysLeft)}`;
  return `D-${daysLeft}`;
};

const formatAgeSummary = (entry: Pick<ImportantDateWithOccurrence, 'occurrence'>) => {
  const { ageCounting, ageMan } = entry.occurrence;

  if (ageCounting === null || ageMan === null) return '출생연도 미입력';

  return `${ageCounting}세(만 ${ageMan}세)`;
};

export const formatImportantDateMetric = (entry: ImportantDateWithOccurrence) => {
  if (entry.type === 'birthday' || entry.type === 'petBirthday') return formatAgeSummary(entry);
  if (entry.type === 'dday') return entry.occurrence.daysLeft < 0 ? '지난 기준일' : '고정 기준일';
  if (entry.occurrence.yearsSince === null) return entry.repeat === 'none' ? '단일 일정' : '연도 미입력';
  return `${entry.occurrence.yearsSince}년째`;
};

export const splitTags = (value: string) =>
  value
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
